import { useEffect, useMemo, useState } from 'react';
import clamp from '~/utils/clamp';

/** Given an array of items and a number of items per page, provides pagination functionality. */
export function usePaginate<T>({
  items,
  perPage,
}: {
  items: T[];
  perPage: number;
}) {
  const [page, setPage] = useState(0);

  useEffect(() => {
    setPage(0);
  }, [items, perPage]);

  return useMemo(() => {
    const pages = Math.ceil(items.length / perPage);

    /** Go to a specific page, but clamps the `index` to valid values; i.e. 0..pages */
    const go = (index: number) => setPage(() => clamp(index, 0, pages - 1));

    const next = () => go(page + 1);
    const previous = () => go(page - 1);

    const start = page * perPage;
    const end = start + perPage;
    const pageItems = items.slice(start, end);

    const isFirst = page === 0;
    const isLast = page === pages - 1;

    return {
      // current page information
      /** Current page index. */
      page,
      /** Items on the current page. */
      pageItems,
      isFirst,
      isLast,
      // navigation functions
      go,
      previous,
      next,
      /**
       * Total number of pages available, given the current `perPage` value.
       * If zero, there are no items.
       * If one, all items fit on a single page.
       */
      pages,
    };
  }, [items, perPage, page]);
}
